import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { EmissionPoint } from '../../financial-configurations/interfaces/financialConfiguration.interfaces';
import {
  CashPointResponse,
  CashPoint,
  PaymentsMethodsReport,
} from '../interfaces/cashPoint.interfaces';
import { ReportDetailResponse } from '../interfaces/reportDetails.interface';

export const cashPointsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['CashPoints', 'CashPointReport'],
});

export const cashPointsApi = cashPointsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET Cash Points
     */
    getCashPoints: builder.query<
      CashPointResponse,
      {
        page: number;
        perPage: number;
        search: string;
        deadline?: string;
        cashier?: string;
        is_open?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { page, search, perPage, deadline, cashier, is_open, sort, type_sort },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<CashPointResponse>('/cash-point', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              ...(sort && { sort }),
              type_sort: type_sort || 'desc',
              search,
              ...(deadline && { deadline }),
              ...(cashier && { cashier }),
              ...(is_open && { is_open }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'CashPoints', id } as const)),
              { type: 'CashPoints', id: 'LIST' },
            ]
          : [{ type: 'CashPoints', id: 'LIST' }],
    }),

    /**
     * @GET Cash Points
     */
    getCashPoint: builder.query<
      CashPoint,
      {
        cashPointId: number;
      }
    >({
      queryFn: async ({ cashPointId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<CashPoint>(`/cash-point/${cashPointId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { cashPointId }) => [{ type: 'CashPoints', cashPointId }],
    }),

    /**
     * @GET Cash Points report
     */
    getCashPointReport: builder.query<
      PaymentsMethodsReport,
      {
        cashPointId: number;
        financialInst?: string;
      }
    >({
      queryFn: async ({ cashPointId, financialInst }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<PaymentsMethodsReport>(
            `/cash-point/${cashPointId}/report`,
            {
              params: {
                user_profile_id,
                type_sort: 'desc',
                ...(financialInst && { financial_institutions_keyword: financialInst }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { cashPointId }) => [{ type: 'CashPointReport', cashPointId }],
    }),

    /**
     * @GET Cash Points report details
     */
    getCashPointReportDetails: builder.query<
      ReportDetailResponse,
      {
        page: number;
        perPage: number;
        search: string;
        cashPointId: number;
        payment_way_keyword: string;
        financial_institutions_keyword?: string;
      }
    >({
      queryFn: async (
        { page, perPage, search, cashPointId, payment_way_keyword, financial_institutions_keyword },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<ReportDetailResponse>(
            `/cash-point/${cashPointId}/report/details`,
            {
              params: {
                user_profile_id,
                page,
                size: perPage,
                type_sort: 'desc',
                search,
                payment_way_keyword,
                ...(financial_institutions_keyword && { financial_institutions_keyword }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { cashPointId }) => [{ type: 'CashPointReport', cashPointId }],
    }),

    /**
     * @POST abrir caja asignada
     */
    openCashPoint: builder.mutation<any, { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>('/cash-point', {
            ...(profileId ? { user_profile_id: profileId } : { user_profile_id }),
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'CashPoints', id: 'LIST' }],
    }),

    /**
     * @POST reabrir caja cerrada
     */
    reOpenCashPoint: builder.mutation<
      any,
      { stablishment_point: string; emission_point: string; user_id: string }
    >({
      queryFn: async ({ stablishment_point, emission_point, user_id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>('/cash-point/supervisor', {
            user_profile_id,
            stablishment_point,
            emission_point,
            user_id,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'CashPoints', id: 'LIST' }],
    }),

    /**
     * @POST cerrar caja/s asignada/s
     */
    closeCashPoint: builder.mutation<
      any,
      { profileId?: number; cashPointsPayload: Partial<number[]> }
    >({
      queryFn: async ({ profileId, cashPointsPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>('/cash-point/cash/close', {
            ...(profileId ? { user_profile_id: profileId } : { user_profile_id }),
            cash_points: [...cashPointsPayload],
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'CashPoints', id: 'LIST' }],
    }),

    /**
     * @GET cash point is available
     */
    getIsAvailableCashPoint: builder.query<EmissionPoint, { profileId: number; userId: number }>({
      queryFn: async ({ profileId, userId }) => {
        try {
          const { data } = await mainApi.get<EmissionPoint>(
            `/cash-point/open-cash/user/${userId}`,
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'CashPoints', id: 'LIST' }],
    }),

    /**
     * @POST Refund
     */
    refundCashPoint: builder.mutation<
      any,
      {
        cash_id: number;
        user_profile_id: number;
      }
    >({
      queryFn: async ({ cash_id, user_profile_id }) => {
        try {
          const { data } = await mainApi.get<any>(`/cash-point/${cash_id}/revert`, {
            params: {
              user_profile_id,
            },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getReportPdf: builder.query<any, { id: number | string; grouped: number }>({
      queryFn: async ({ id, grouped }) => {
        try {
          const { data } = await mainApi.get<any>(`/cash-point/${id}/report-pdf`, {
            responseType: 'arraybuffer',
            params: {
              grouped,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    // /**
    //  * @GET Financial periods by id
    //  */
    // getFinancialPeriod: builder.query<FinancialPeriod, { profileId: number; fperiodId: number }>({
    //   queryFn: async ({ profileId, fperiodId }) => {
    //     try {
    //       const { data } = await mainApi.get<FinancialPeriod>(`/financial-periods/${fperiodId}`, {
    //         params: {
    //           user_profile_id: profileId,
    //         },
    //       });

    //       return { data };
    //     } catch (error: any) {
    //       return { error };
    //     }
    //   },
    //   providesTags: (result, errro, { fperiodId }) => [
    //     // { type: 'CashPoints', id: 'LIST' }, // optional, invalidate on all process
    //     { type: 'CashPoints', fperiodId }, // invalidate only with period Id
    //   ],
    // }),

    // /**
    //  * @GET all Financial periods Availables
    //  */
    // getAvailablesFinancialPeriods: builder.query<FinancialPeriod[], { profileId: Number }>({
    //   queryFn: async ({ profileId }) => {
    //     try {
    //       const { data } = await mainApi.get<FinancialPeriod[]>('/financial-periods', {
    //         params: {
    //           user_profile_id: profileId,
    //           data: 'all',
    //         },
    //       });

    //       return { data: Array.isArray(data) ? data : [] };
    //     } catch (error: any) {
    //       return { error };
    //     }
    //   },
    //   providesTags: [{ type: 'FinancialPeriods', id: 'LIST' }],
    // }),

    // /**
    //  * @PUT update financial period
    //  */
    // updateFinancialPeriod: builder.mutation<
    //   any,
    //   {
    //     profileId: number;
    //     fperiodId: number;
    //     financialPeriodPayload: Partial<FinancialPeriodsPayload>;
    //   }
    // >({
    //   queryFn: async ({ profileId, fperiodId, financialPeriodPayload }) => {
    //     try {
    //       const { data } = await mainApi.put<any>(`/financial-periods/${fperiodId}`, {
    //         user_profile_id: profileId,
    //         ...financialPeriodPayload,
    //       });

    //       return { data };
    //     } catch (error: any) {
    //       return { error };
    //     }
    //   },
    //   invalidatesTags: (result, error, { fperiodId }) => [
    //     { type: 'FinancialPeriods', id: 'LIST' }, //invalidate all
    //     { type: 'FinancialPeriods', fperiodId }, // invalidate only with period Id
    //   ],
    // }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCashPointsQuery,
  useGetCashPointQuery,
  useLazyGetCashPointQuery,
  useGetCashPointReportQuery,
  useGetCashPointReportDetailsQuery,
  useGetIsAvailableCashPointQuery,
  useOpenCashPointMutation,
  useReOpenCashPointMutation,
  useCloseCashPointMutation,
  useRefundCashPointMutation,
  useLazyGetReportPdfQuery,
} = cashPointsApi;
